import { useLocation, useNavigationType, useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPartnerShopList, getPartnerShopListAreas } from '@/api/partnerShops';
import type { PartnerShopAreaOption, PartnerShopListItem } from '@/types/partnerShop';
import PartnerShopPrevMobileCard from './components/PartnerShopPrevMobileCard';

const CATEGORY_META: Record<string, {
  label: string;
  title: string;
  description: string;
}> = {
  suit: {
    label: '예복',
    title: '예복 인증업체',
    description: '후기와 사진으로 비교하는 예복 인증업체',
  },
  jewelry: {
    label: '예물',
    title: '예물 인증업체',
    description: '후기와 사진으로 비교하는 예물 인증업체',
  },
  hanbok: {
    label: '한복',
    title: '한복 인증업체',
    description: '후기와 사진으로 비교하는 한복 인증업체',
  },
};

const REGION_LABELS: Record<string, string> = {
  '청담동': '청담',
  '압구정동': '압구정',
  '신사동 가로수길': '신사/가로수길',
  '논현동': '논현',
};

function getRegionLabel(region: string): string {
  return REGION_LABELS[region] || region;
}

function getTotalAreaCount(areas: PartnerShopAreaOption[]): number {
  const allArea = areas.find((area) => area.areaName === '전체');
  if (allArea) return allArea.count;

  return areas.reduce((sum, area) => sum + area.count, 0);
}

function sortAreasForDisplay(areas: PartnerShopAreaOption[]): PartnerShopAreaOption[] {
  return [...areas].sort((a, b) => {
    if (a.areaName === '기타') return 1;
    if (b.areaName === '기타') return -1;
    return 0;
  });
}

function SkeletonList() {
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      {[1, 2, 3, 4].map((value) => (
        <div key={value} className="overflow-hidden rounded-2xl bg-white animate-pulse">
          <div className="aspect-[16/9] bg-[#F4F4F5]" />
          <div className="px-5 pb-5 pt-[18px]">
            <div className="h-[22px] bg-[#F4F4F5] rounded w-1/2 mb-1" />
            <div className="h-3 bg-[#F4F4F5] rounded w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onReset }: { onReset: () => void }) {
  return (
    <div className="py-16 text-center">
      <p className="text-headline2 text-label-normal mb-2">조건에 맞는 인증업체가 없습니다.</p>
      <p className="text-body2 text-label-alt mb-5">지역 조건을 다시 선택해 주세요.</p>
      <button
        onClick={onReset}
        className="inline-flex items-center justify-center h-10 px-5 rounded-full bg-primary-normal text-static-white text-sm font-medium cursor-pointer"
      >
        전체 업체 보기
      </button>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="py-16 text-center">
      <p className="text-headline2 text-label-normal mb-2">업체 목록을 불러오지 못했습니다.</p>
      <p className="text-body2 text-label-alt mb-5">{message}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center justify-center h-10 px-5 rounded-full bg-primary-normal text-static-white text-sm font-medium cursor-pointer"
      >
        다시 시도
      </button>
    </div>
  );
}

export default function DressList() {
  const { categoryKey = 'suit' } = useParams<{ categoryKey: string }>();
  const location = useLocation();
  const navigationType = useNavigationType();
  const meta = CATEGORY_META[categoryKey] || CATEGORY_META.suit;

  const [loading, setLoading] = useState(true);
  const [areaLoading, setAreaLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [shops, setShops] = useState<PartnerShopListItem[]>([]);
  const [areas, setAreas] = useState<PartnerShopAreaOption[]>([
    {
      areaName: '전체',
      count: 0,
    },
  ]);
  const [shopCategory, setShopCategory] = useState(meta.label);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedArea, setSelectedArea] = useState('전체');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [filterStuck, setFilterStuck] = useState(false);

  const visibleAreas = useMemo(() => {
    return sortAreasForDisplay(areas.filter((area) => area.areaName !== '전체'));
  }, [areas]);

  const selectedAreaLabel = selectedArea !== '전체'
    ? getRegionLabel(selectedArea)
    : '지역 전체';

  const filteredCount = shops.length;

  const sheetRef = useRef<HTMLDivElement>(null);
  const filterTopRef = useRef<HTMLElement>(null);
  const filterSentinelRef = useRef<HTMLDivElement>(null);
  const didApplyInitialScrollRef = useRef(false);

  useEffect(() => {
    if (!sheetOpen) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSheetOpen(false);
    };

    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [sheetOpen]);

  useEffect(() => {
    const sentinel = filterSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setFilterStuck(!entry.isIntersecting);
      },
      {
        rootMargin: '-64px 0px 0px 0px',
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, []);

  const fetchAreas = useCallback(async () => {
    setAreaLoading(true);

    try {
      const { data } = await getPartnerShopListAreas(categoryKey);
      const nextAreas = data.areas.length > 0
        ? data.areas
        : [
          {
            areaName: '전체',
            count: 0,
          },
        ];

      setShopCategory(data.shopCategory);
      setAreas(nextAreas);
      setTotalCount(getTotalAreaCount(nextAreas));
    } catch (error) {
      console.error('[DressList] failed to fetch areas', error);

      setAreas([
        {
          areaName: '전체',
          count: 0,
        },
      ]);
    } finally {
      setAreaLoading(false);
    }
  }, [categoryKey]);

  const fetchShops = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const { data } = await getPartnerShopList({
        categoryKey,
        areaName: selectedArea,
      });
      console.log(data)

      setShopCategory(data.shopCategory);
      setShops(data.list);
      if (selectedArea === '전체') {
        setTotalCount(data.total);
      }
    } catch (error) {
      console.error('[DressList] failed to fetch shops', error);

      setShops([]);
      setTotalCount(0);
      setErrorMessage('잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  }, [categoryKey, selectedArea]);

  useEffect(() => {
    setSelectedArea('전체');
    fetchAreas();
  }, [categoryKey, fetchAreas]);

  useEffect(() => {
    fetchShops();
  }, [fetchShops]);

  useEffect(() => {
    didApplyInitialScrollRef.current = false;
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (didApplyInitialScrollRef.current) return;
    if (loading) return;

    didApplyInitialScrollRef.current = true;

    const currentListPath = `${location.pathname}${location.search}`;
    const savedReturnPath = sessionStorage.getItem('partner-shop-list:returnPath');
    const savedScrollY = Number(sessionStorage.getItem('partner-shop-list:scrollY') || 0);
    const shouldRestoreScroll =
      navigationType === 'POP' &&
      savedReturnPath === currentListPath &&
      Number.isFinite(savedScrollY) &&
      savedScrollY > 0;

    window.scrollTo({
      top: shouldRestoreScroll ? savedScrollY : 0,
      left: 0,
    });

    sessionStorage.removeItem('partner-shop-list:returnPath');
    sessionStorage.removeItem('partner-shop-list:scrollY');
  }, [loading, location.pathname, location.search, navigationType]);

  const selectArea = (areaName: string) => {
    setSelectedArea(areaName);
    setSheetOpen(false);

    filterTopRef.current?.scrollIntoView({
      behavior: 'auto',
      block: 'start',
    });
  };

  const handleResetAll = () => {
    selectArea('전체');
  };

  return (
    <div className="min-h-screen bg-bg-normal">
      {/* Header */}
      <section className="pt-24 md:pt-17 pb-2 px-5 md:px-6">
      {/* <section className="pt-6 md:pt-6 pb-5 px-5 md:px-6"> */}
        <div className="max-w-[1100px] mx-auto">
          <span className="inline-block px-3 py-1 rounded-full bg-fill-normal text-accent-brown text-caption font-medium mb-3">
            {shopCategory || meta.label}
          </span>
          <h1 className="text-heading2 md:text-title2 text-label-normal mb-2">
            {shopCategory || meta.label} 인증업체
          </h1>
          <p className="text-body2 md:text-body1 text-label-neutral">
            {meta.description}
          </p>
        </div>
      </section>

      {/* Filter Row */}
      <div ref={filterSentinelRef} className="h-px" />
      <section
        ref={filterTopRef}
        className={`sticky pt-2 top-[64px] md:top-[64px] z-20 bg-bg-normal pb-2 md:pb-2 px-5 md:px-6 lg:static lg:z-auto scroll-mt-[60px] lg:py-1 md:scroll-mt-[64px] transition-colors ${
          filterStuck ? 'border-b border-line-divider lg:border-b-0' : 'border-b border-transparent'
        }`}
      >
        <div className="max-w-[1100px] mx-auto">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Region Button — mobile only */}
            <button
              onClick={() => setSheetOpen(true)}
              className="lg:hidden inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-line-normal bg-static-white text-label-neutral text-sm font-medium hover:border-line-alt transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>{selectedAreaLabel}</span>
              <i className="ri-arrow-down-s-line text-base text-label-alt"></i>
            </button>
            {/* Count */}
            {/* <p className="text-sm text-label-alt whitespace-nowrap">
              {selectedArea !== '전체'
                ? `${getRegionLabel(selectedArea)} ${filteredCount}곳`
                : `전체 ${shopCategory || meta.label} 인증업체 ${totalCount}곳`}
            </p> */}
            {areaLoading && (
              <p className="text-xs text-label-alt whitespace-nowrap">지역 불러오는 중</p>
            )}
          </div>
        </div>
      </section>

      {/* Bottom Sheet Overlay */}
      {sheetOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSheetOpen(false);
          }}
        >
          {/* Dim */}
          <div className="absolute inset-0 bg-black/30" onClick={() => setSheetOpen(false)} />

          {/* Sheet */}
          <div
            ref={sheetRef}
            className="relative w-full max-w-[500px] bg-static-white rounded-t-[20px] max-h-[70vh] overflow-y-auto animate-[slideUp_0.25s_ease-out]"
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-line-normal" />
            </div>

            <div className="px-5 pb-8 pt-3">
              <h3 className="text-headline2 text-label-normal mb-4">지역 선택</h3>

              {/* 2-column grid */}
              <div className="grid grid-cols-2 gap-2">
                {/* 전체 */}
                <button
                  onClick={() => selectArea('전체')}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer ${
                    selectedArea === '전체'
                      ? 'bg-primary-normal text-static-white'
                      : 'bg-bg-alt text-label-neutral hover:bg-fill-normal'
                  }`}
                >
                  <span>전체</span>
                  <span className={`text-xs ${selectedArea === '전체' ? 'text-static-white/70' : 'text-label-alt'}`}>
                    {totalCount}
                  </span>
                </button>
                {visibleAreas.map((area) => (
                  <button
                    key={area.areaName}
                    onClick={() => selectArea(area.areaName)}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer ${
                      selectedArea === area.areaName
                        ? 'bg-primary-normal text-static-white'
                        : 'bg-bg-alt text-label-neutral hover:bg-fill-normal'
                    }`}
                  >
                    <span>{getRegionLabel(area.areaName)}</span>
                    <span className={`text-xs ${selectedArea === area.areaName ? 'text-static-white/70' : 'text-label-alt'}`}>
                      {area.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <section className="pt-8 pb-16 md:pb-20 px-5 md:px-6">
        <div className="max-w-[1100px] mx-auto">
          <div className="lg:grid lg:grid-cols-[180px_minmax(0,1fr)] lg:gap-10 lg:items-start">

            {/* Left Sidebar - Desktop Region Filter */}
            <aside className="hidden lg:block sticky top-24">
              <h4 className="text-[13px] font-semibold text-label-normal mb-3">지역</h4>
              <nav className="flex flex-col gap-1">
                <button
                  onClick={() => selectArea('전체')}
                  className={`flex items-center justify-between w-full text-left px-3 py-2 rounded-lg text-[14px] font-medium transition-colors cursor-pointer ${
                    selectedArea === '전체'
                      ? 'bg-fill-normal text-accent-brown'
                      : 'text-label-neutral hover:bg-bg-alt'
                  }`}
                >
                  <span>전체</span>
                  <span className="text-xs text-label-alt ml-2">{totalCount}</span>
                </button>
                {visibleAreas.map((area) => (
                  <button
                    key={area.areaName}
                    onClick={() => selectArea(area.areaName)}
                    className={`flex items-center justify-between w-full text-left px-3 py-2 rounded-lg text-[14px] font-medium transition-colors cursor-pointer ${
                      selectedArea === area.areaName
                        ? 'bg-fill-normal text-accent-brown'
                        : 'text-label-neutral hover:bg-bg-alt'
                    }`}
                  >
                    <span>{getRegionLabel(area.areaName)}</span>
                    <span className="text-xs text-label-alt ml-2">{area.count}</span>
                  </button>
                ))}
              </nav>
            </aside>

            {/* Vendor List */}
            <div>
              {/* Vendor Cards */}
              {loading ? (
                <SkeletonList />
              ) : errorMessage ? (
                <ErrorState message={errorMessage} onRetry={fetchShops} />
              ) : shops.length === 0 ? (
                <EmptyState onReset={handleResetAll} />
              ) : (
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  {shops.map((shop) => (
                    <PartnerShopPrevMobileCard key={shop.id} shop={shop} />
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* Bottom sheet slide-up animation */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
